import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { chakra, defineRecipe, useRecipe } from '@chakra-ui/react'

// C_Button-Tiles primitive — see docs/design/C_Button-Tiles-design-spec.md
// Recipe co-located per repo convention (bare token names; border shorthand to
// dodge the v3 three-prop border bug). Consumed via useRecipe({recipe}) — fine
// under the single root ChakraProvider.

export type ButtonTileVariant = 'suggested'

const buttonTileRecipe = defineRecipe({
  className: 'careos-button-tile',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'body',
    borderRadius: 'sm',
    padding: '8px 10px',
    gap: '4px',
    cursor: 'pointer',
    border: 'none',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
    _disabled: { cursor: 'not-allowed', opacity: 0.4 },
  },
  variants: {
    variant: {
      suggested: {
        background: 'canvas',
        border: '1px solid',
        borderColor: 'bluegrey',
        color: 'evergreen',
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '1.25',
        letterSpacing: '0.01em',
        '& .btn-tile-eyebrow': { color: 'bluegrey' },
        _hover: { borderColor: 'aqua' },
      },
    },
    selected: { true: {}, false: {} },
  },
  compoundVariants: [
    {
      variant: 'suggested',
      selected: false,
      css: { '& .btn-tile-end-icon': { color: 'seafoam' } },
    },
    {
      variant: 'suggested',
      selected: true,
      css: {
        background: 'seafoam',
        borderColor: 'transparent',
        color: 'white',
        fontWeight: '600',
        _hover: { background: 'aqua' },
        '& .btn-tile-end-icon': { color: 'currentColor' },
      },
    },
  ],
  defaultVariants: { variant: 'suggested', selected: false },
})

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant: ButtonTileVariant
  selected?: boolean
  /** Small category label rendered to the LEFT of the main label.
   *  Auto-suppressed when `selected` is true (per Figma — selected drops the eyebrow). */
  eyebrow?: string
  /** Main label text. */
  label: string
  /** Trailing icon (e.g. <PlusIcon size={15} /> idle, <CheckIcon size={15} /> selected). */
  endIcon?: ReactNode
}

export function ButtonTile({
  variant,
  selected = false,
  eyebrow,
  label,
  endIcon,
  ...rest
}: Props) {
  const recipe = useRecipe({ recipe: buttonTileRecipe })
  const styles = recipe({ variant, selected })

  return (
    <chakra.button type="button" css={styles} aria-pressed={selected} {...rest}>
      {eyebrow && !selected && (
        <chakra.span className="btn-tile-eyebrow">{eyebrow}</chakra.span>
      )}
      <chakra.span>{label}</chakra.span>
      {endIcon && (
        <chakra.span
          className="btn-tile-end-icon"
          css={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {endIcon}
        </chakra.span>
      )}
    </chakra.button>
  )
}
